<template>
  <div v-if="user" class="view-adminlicensecreate">
    <div class="breadcrump-trail">
      <router-link :to="{ name: 'admin-home' }"> Admin </router-link> /
      <router-link :to="{ name: 'admin-users-list' }"> Nutzer </router-link> /
      <router-link :to="{ name: 'admin-users-update', params: { userId } }">
        {{ userId }}
      </router-link>
      / Abo bearbeiten
    </div>

    <h1>Abo für {{ user.name }} ({{ user.email }}) bearbeiten</h1>

    <div v-if="subscription?.stripeSubscriptionId" class="message-warning p-4 border-warning">
      <lucide-icon name="triangle-alert" />
      <div>
        <p>
          Dieses Abo ist bereits aktiv und wird über Stripe verwaltet. Bitte alle Änderungen auch <a :href="`https://dashboard.stripe.com/subscriptions/${subscription.stripeSubscriptionId}`" target="_blank">in Stripe</a> vornehmen,
          um Inkonsistenzen zu vermeiden.
        </p>
        <p>
          Jede Änderung an diesem Abo ist eine <strong>VERTRAGSÄNDERUNG</strong> und sollte mit dem
          Kunden abgesprochen werden.
        </p>
      </div>
    </div>

    <form
      class="column view-adminlicensecreate__form"
      @submit.prevent="updateSubscription"
    >
      <h3>{{ $t(`subscriptions.${plan?.id}.name`) }}</h3>

      <input-dropdown
        v-model:value="data.paymentPeriod"
        :label="$t('labels.paymentPeriod')"
        :options="['year', 'semester', 'month']"
      >
        <template #default="{ item }">
          {{ $t(`periods.${item}`) }}
        </template>
      </input-dropdown>

      <hr />

      <div class="flex gap-4">
        <input-text
          :value="data.lexofficeContactId || undefined"
          :label="`Lexoffice Kontakt-ID`"
          @update:value="data.lexofficeContactId = ($event || null)"
        />
        <a
          :href="`https://app.lexware.de/permalink/contacts/view/${data.lexofficeContactId}`"
          target="_blank"
          class="button-icon"
        >
          <lucide-icon name="square-arrow-out-up-right" />
        </a>
      </div>

      <hr />

      <div class="message-notice p-4" v-if="subscription?.stripeSubscriptionId != null">
        Die Verlängerung dieses Abos wird von Stripe gemanaged, alle folgenden Einstellungen
        haben keine Auswirkungen auf die Verlängerung und sind daher deaktiviert.
      </div>

      <input-checkbox
        :value="data.usesStandardPrices"
        :disabled="subscription?.stripeSubscriptionId != null"
        class="mv-4"
        @update:value="data.usesStandardPrices = data.editable = data.invoiceAutomatically = $event"
      >
        <strong>Nutzt Standardpreise</strong>
        <div class="mt-1">
          Abstellen, falls Rabatte gewährt wurden oder Sonderkonditionen gelten!
        </div>
      </input-checkbox>

      <input-checkbox
        :value="data.editable && data.usesStandardPrices"
        :disabled="subscription?.stripeSubscriptionId != null || !data.usesStandardPrices"
        class="mb-4"
        @update:value="data.editable = data.invoiceAutomatically = $event"
      >
        <strong>Vom Kunden änderbar</strong> <i v-if="!data.usesStandardPrices">(Nur mit Standardpreisen)</i>
        <div class="mt-1">
          Kunde kann selbständig verlängern und Nachbestellungen tätigen.
        </div>
      </input-checkbox>

      <input-checkbox
        :value="data.invoiceAutomatically && data.editable && data.usesStandardPrices"
        :disabled="subscription?.stripeSubscriptionId != null || !data.editable || !data.usesStandardPrices"
        class="mb-4"
        @update:value="data.invoiceAutomatically = $event"
      >
        <strong>Automatisch Rechnungen versenden</strong> <i v-if="!data.editable">(Nur wenn bearbeitbar)</i>
        <div class="mt-1">
          Erstellt und verschickt bei Verlängerung automatisch eine Rechnung aus LexOffice an die unten angegebene Adresse.
        </div>
        <div class="mt-1" v-show="!data.invoiceAutomatically && data.editable && data.usesStandardPrices">
          <strong>Bitte nur abwählen, wenn selbst Rechnungen verschickt werden. Nachbestellungen senden trotzdem Rechnungen!</strong>
        </div>
      </input-checkbox>

      <hr />


      <input-text
        v-if="plan?.subscriptionType == 'organization'"
        v-model:value="data.name"
        :label="$t('labels.organizationName')"
        :required="true"
        :error="fieldErrors?.name"
        autocomplete="new-organization-name"
      />

      <hr />

      <div v-if="plan?.canAddSeats" class="line_flex">
        <input-number
          :value="data.seats"
          :min="1"
          :label="$t('labels.licenseAmount')"
          :step="1"
          @update:value="data.seats = $event"
        />

        <input-number
          v-if="plan.canAddWorkspaces"
          :value="data.extraWorkspaces + 1"
          :label="$t('words.workspace', 2)"
          :min="1"
          :step="1"
          @update:value="data.extraWorkspaces = $event - 1"
        />
      </div>

      <div v-if="plan?.upgradeable">
        <h4 class="label mt-2 mb-2">{{ $t("headings.upgrades") }}</h4>
        <div class="column">
          <div v-for="upgrade of subscriptionUpgrades">
            <input-checkbox
              :value="data.upgrades.includes(upgrade.id)"
              :label="$t(`upgrades.${upgrade.id}.name`)"
              @update:value="setUpgrade(upgrade.id, $event)"
            />
          </div>
        </div>
      </div>

      <div>
        <h4 class="label">Nächste Verlängerung</h4>
        <input-checkbox
          v-model:value="data.renewAutomatically"
          :label="$t('labels.automaticRenewal')"
        />
      </div>

      <fieldset v-if="data.renewAutomatically" class="mt-2 ml-4">
        <legend>Verlängerungseinstellungen</legend>
        <div class="mt-2 mb-4">
          Die nächste Verlängerung findet statt am <span v-if="subscription?.validUntil != null">{{ formatDateString(subscription?.validUntil) }}</span>
        </div>
        <input-checkbox
          :value="data.renewOnce"
          label="Einmalig verlängern"
          @update:value="data.renewOnce = $event"
        />
        <input-number
          class="mt-4"
          :value="data.nextRenewSeats"
          :min="1"
          :label="$t('labels.licenseAmount')"
          :step="1"
          @update:value="data.nextRenewSeats = $event"
        />

        <div v-if="plan?.upgradeable">
          <h4 class="label mt-2 mb-2">{{ $t("headings.upgrades") }}</h4>
          <div class="column">
            <div v-for="upgrade of subscriptionUpgrades">
              <input-checkbox
                :value="data.nextRenewUpgrades.includes(upgrade.id) && data.upgrades.includes(upgrade.id)"
                :label="$t(`upgrades.${upgrade.id}.name`)"
                :disabled="!data.upgrades.includes(upgrade.id)"
                @update:value="setRenewUpgrade(upgrade.id, $event)"
              />
            </div>
          </div>
        </div>
      </fieldset>

      <hr />

      <h4 class="mb-0 mt-0">{{ $t("headings.billingAddress") }}</h4>
      <input-text
        v-model:value="data.email"
        :label="$t('labels.email')"
        :required="true"
      />
      <address-form-fields
        v-model:firstname="data.firstname"
        v-model:lastname="data.lastname"
        v-model:company="data.company"
        v-model:vatId="data.vatId"
        v-model:street="data.street"
        v-model:supplement="data.supplement"
        v-model:zipcode="data.zipcode"
        v-model:city="data.city"
        v-model:country="data.country"
        :company-required="false"
        :required="false"
        :show-form-errors="true"
      />
      
      <hr />

      <h4 class="mv-0">Weiteres</h4>

      <input-text
        v-model:value="data.validUntil"
        :label="$t('labels.validUntil')"
        type="date"
      />

      <input-checkbox
        v-model:value="data.renewAutomatically"
        :label="$t('labels.automaticRenewal')"
      />

      <button class="button mt-4" type="submit">Abo ändern</button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { BadRequestError } from "@core/errors/Errors";
import { ensureInt } from "@/helpers/utility/Type";
import { useAdmin } from "./useAdmin";
import { formatDateString } from "@/i18n";

import LucideIcon from "@core/ui/LucideIcon.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import InputText from "@core/input/InputText.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import AddressFormFields from "@manager/common/AddressFormFields.vue";

import { usePlans, subscriptionUpgrades } from "@manager/plans/plans.store";
import { showConfirm, showError } from "@core/ui/alert/alert";

import type { AdminUser } from "@/api/User";
import type { LicensePlan } from "@manager/plans/plans.store";

const props = defineProps<{ userId?: string; subscriptionId?: string }>();

const router = useRouter();
const adminAPI = useAdmin();
const { plans } = usePlans();

const user = ref<AdminUser | null>(null);
const subscription = ref<NonNullable<AdminUser["subscriptions"]>[number] | null>(null);
const plan = ref<LicensePlan | null>(null);
const data = reactive({
  paymentPeriod: "year" as "year" | "semester" | "month",
  seats: 1,
  extraWorkspaces: 0,
  upgrades: [] as string[],

  nextRenewSeats: 1,
  nextRenewUpgrades: [] as string[],
  nextRenewYears: 1,
  renewOnce: false,

  name: "",

  email: "",
  firstname: "",
  lastname: "",
  company: "",
  vatId: "",
  street: "",
  supplement: "",
  zipcode: "",
  city: "",
  country: "",

  lexofficeContactId: null as null | string,

  validUntil: undefined as undefined | string,
  minValidUntilDate: new Date().toISOString().split("T")[0],
  renewAutomatically: false,
  usesStandardPrices: false,
  editable: false,
  invoiceAutomatically: false
});

const fieldErrors = ref<{ [key: string]: string }>({});

loadUser();

async function loadUser() {
  const loadedUser = await adminAPI.get<AdminUser>(`/users/${props.userId}`);
  user.value = loadedUser;

  data.email = loadedUser.email;

  const subscriptions = loadedUser.subscriptions || [];
  subscription.value = subscriptions.find(sub => sub.id == ensureInt(props.subscriptionId || "")) ?? null;

  console.log("subscription", subscription.value);

  if (subscription.value) {
    const sub = subscription.value;

    plan.value = plans[sub.plan as keyof typeof plans] || null;
    data.seats = sub.seats || 1;
    data.upgrades = sub.upgrades.slice();

    data.paymentPeriod = sub.paymentPeriod;
    data.company = sub.billingAddress?.company || "";
    data.vatId = sub.billingAddress?.vatId || "";
    data.zipcode = sub.billingAddress?.zipcode || "";
    data.city = sub.billingAddress?.city || "";
    data.country = sub.billingAddress?.country || "";
    data.firstname = sub.billingAddress?.firstname || "";
    data.lastname = sub.billingAddress?.lastname || "";
    data.street = sub.billingAddress?.street || "";
    data.supplement = sub.billingAddress?.supplement || "";

    data.name = sub.organization?.name || "";

    data.lexofficeContactId = sub.lexofficeContactId || null;

    data.nextRenewSeats = sub.nextRenew.seats || data.seats;
    data.nextRenewUpgrades = (sub.nextRenew.upgrades || data.upgrades).slice();
    data.nextRenewYears = sub.nextRenew.years || 1;

    if (sub.validUntil) {
      data.validUntil = sub.validUntil.split("T")[0];
    }
    data.renewAutomatically = sub.renewAutomatically == true;
    data.renewOnce = sub.renewOnce == true;
    data.usesStandardPrices = sub.usesStandardPrices || false;
    data.editable = sub.editable || false;
    data.invoiceAutomatically = sub.invoiceAutomatically || false;
  }
}

function setUpgrade(upgradeId: string, value: boolean) {
  if (value) {
    data.upgrades.push(upgradeId);
  } else {
    data.upgrades = data.upgrades.filter(id => id !== upgradeId);
  }
}
function setRenewUpgrade(upgradeId: string, value: boolean) {
  if (value) {
    data.nextRenewUpgrades.push(upgradeId);
  } else {
    data.nextRenewUpgrades = data.nextRenewUpgrades.filter(id => id !== upgradeId);
  }
}

async function updateSubscription() {

  if (!await showConfirm("Du bist sicher, dass du das Abo ändern möchtest und eine Vertragsänderung durchführen willst?", "Ja, Abo ändern")) {
    return;
  }

  try {
    if (plan.value == null) {
      throw new Error("Bitte Plan auswählen.");
    }

    if (data.paymentPeriod == null) {
      throw new Error("Bitte Zahlungsperiode auswählen.");
    }

    const subscriptionData: {
      plan: string;
      name: string;
      email: string;
      renewAutomatically: boolean;
      paymentPeriod: string;
      seats: number;
      upgrades: string[];

      lexofficeContactId: string | null;

      billingAddress: {
        company?: string;
        vatId?: string;
        
        firstname: string;
        lastname: string;
  
        street: string;
        supplement: string;
        
        zipcode: string;
        city: string;
        country: string;
      }

      validUntil?: string;
      usesStandardPrices: boolean;
      editable: boolean;
      invoiceAutomatically: boolean;
    } = {
      plan: plan.value.id,
      renewAutomatically: data.renewAutomatically,

      name: data.name,

      email: data.email,
      paymentPeriod: data.paymentPeriod,

      seats: plan.value.canAddSeats ? data.seats : 1,
      upgrades: data.upgrades.slice(),

      seatsOnRenew: plan.value.canAddSeats ? data.nextRenewSeats : 1,
      upgradesOnRenew: data.nextRenewUpgrades.slice(),
      renewOnce: data.renewOnce,

      lexofficeContactId: data.lexofficeContactId || null,
      
      billingAddress: {
        company: data.company,
        vatId: data.vatId,

        firstname: data.firstname,
        lastname: data.lastname,

        street: data.street,
        supplement: data.supplement,
        zipcode: data.zipcode,
        city: data.city,
        country: data.country
      },
      usesStandardPrices: data.usesStandardPrices,
      editable: data.editable && data.usesStandardPrices,
      invoiceAutomatically: data.invoiceAutomatically && data.editable && data.usesStandardPrices
    };

    if (data.validUntil) {
      const validUntil = new Date(data.validUntil);
      subscriptionData.validUntil = validUntil.toISOString();
    }

    await adminAPI.put(`/users/${props.userId}/subscriptions/${props.subscriptionId}`, { data: subscriptionData });

    router.push({
      name: "admin-users-update",
      params: { userId: props.userId }
    });
  } catch (_error) {
    const error = _error as Error | BadRequestError;
    fieldErrors.value = "fields" in error ? error?.fields : {};

    console.log(error);
    showError(error);
  }
}
</script>

<style lang="scss">
.view-adminlicensecreate {
  max-width: 75rem;
  width: 100%;
  padding: 2rem;

  &__form {
    max-width: 42rem;
  }
}
</style>
